import * as React from 'react';

type StepTimerOptions = {
  /** How long each step stays, in milliseconds. One entry per step. */
  durations: readonly number[];
  /**
   * Whether the clock is running. Off, it holds where it is, and picks up from
   * there when it is switched back on rather than starting the step again.
   */
  playing?: boolean;
  /**
   * The finest step `elapsed` is reported in, in milliseconds. Everything that
   * reads it (a typed sentence, a progress bar) is re-rendered on each one, so
   * this is the trade between smoothness and work. 30ms is a typed character
   * at a time and a bar that looks continuous.
   */
  resolution?: number;
};

/**
 * One clock for a set of steps that advance on their own: which step is on,
 * and how far into it we are. Loops from the last back to the first.
 *
 * One clock rather than one per thing that moves, so a sentence typing itself
 * out and a bar filling beside it cannot drift apart: both are drawn from the
 * same `elapsed`. A `requestAnimationFrame` loop rather than an interval, so it
 * stops on its own in a background tab.
 *
 * `go` jumps to a step and starts its clock again, including the one already
 * on: a click on the current step restarts it.
 */
export function useStepTimer({ durations, playing = true, resolution = 30 }: StepTimerOptions) {
  const count = durations.length;
  // `run` counts every change of step. It is what restarts the loop when the
  // step advances to the same index (a set of one) or `go` picks the current
  // one, where the index alone would not change.
  const [state, setState] = React.useState({ index: 0, elapsed: 0, run: 0 });
  // The last reported `elapsed`, so a paused clock resumes from it.
  const elapsedRef = React.useRef(0);

  const index = count > 0 ? Math.min(state.index, count - 1) : 0;
  const duration = durations[index] ?? 0;

  React.useEffect(() => {
    if (!playing || count === 0 || !(duration > 0)) return;

    const origin = performance.now() - elapsedRef.current;
    let frame = 0;

    const tick = (now: number) => {
      const elapsed = now - origin;
      if (elapsed >= duration) {
        elapsedRef.current = 0;
        setState((s) => ({ index: (index + 1) % count, elapsed: 0, run: s.run + 1 }));
        return;
      }
      if (elapsed - elapsedRef.current >= resolution) {
        elapsedRef.current = elapsed;
        setState((s) => ({ ...s, elapsed }));
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, count, index, duration, resolution, state.run]);

  const go = React.useCallback(
    (next: number) => {
      if (count === 0) return;
      elapsedRef.current = 0;
      setState((s) => ({ index: ((next % count) + count) % count, elapsed: 0, run: s.run + 1 }));
    },
    [count],
  );

  return {
    index,
    elapsed: state.elapsed,
    duration,
    /** How far through the current step, from 0 to 1. */
    progress: duration > 0 ? Math.min(state.elapsed / duration, 1) : 0,
    go,
  };
}
